/**
 * yd-about-bg-canvas.js
 * WebGL noise gradient 背景，仅在 about-bg-canvas section 启用时通过 section 自身 <script> 加载。
 * 多级降级：
 *   1. data-mobile-disabled + viewport < 768 → 不启动
 *   2. prefers-reduced-motion → 不启动
 *   3. WebGL context 创建失败 → 不启动
 * 兜底：CSS 中 .yd-about-bg-canvas 自带径向渐变背景；启动成功后元素加 .is-active class 隐藏渐变。
 * 兼容主题编辑器：监听 shopify:section:load 重新扫描 canvas。
 */
(function () {
  function startBgCanvas(canvas) {
    if (canvas.dataset.ydAboutStarted === '1') return;

    if (canvas.dataset.mobileDisabled === 'true' && window.innerWidth < 768) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return;

    canvas.dataset.ydAboutStarted = '1';
    canvas.classList.add('is-active');

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    }
    const ac = new AbortController();
    window.addEventListener('resize', resize, { signal: ac.signal });
    resize();

    const vsSource = '\n\
      attribute vec4 aVertexPosition;\n\
      void main() { gl_Position = aVertexPosition; }\n\
    ';
    const fsSource = '\n\
      precision highp float;\n\
      uniform vec2 u_resolution;\n\
      uniform float u_time;\n\
      vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }\n\
      vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }\n\
      vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }\n\
      float snoise(vec2 v) {\n\
        const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);\n\
        vec2 i  = floor(v + dot(v, C.yy));\n\
        vec2 x0 = v - i + dot(i, C.xx);\n\
        vec2 i1;\n\
        i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);\n\
        vec4 x12 = x0.xyxy + C.xxzz;\n\
        x12.xy -= i1;\n\
        i = mod289(i);\n\
        vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));\n\
        vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);\n\
        m = m*m; m = m*m;\n\
        vec3 x = 2.0 * fract(p * C.www) - 1.0;\n\
        vec3 h = abs(x) - 0.5;\n\
        vec3 ox = floor(x + 0.5);\n\
        vec3 a0 = x - ox;\n\
        m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);\n\
        vec3 g;\n\
        g.x  = a0.x  * x0.x  + h.x  * x0.y;\n\
        g.yz = a0.yz * x12.xz + h.yz * x12.yw;\n\
        return 130.0 * dot(m, g);\n\
      }\n\
      void main() {\n\
        vec2 st = gl_FragCoord.xy / u_resolution.xy;\n\
        st.x *= u_resolution.x / u_resolution.y;\n\
        vec3 color = vec3(0.008, 0.008, 0.016);\n\
        float n1 = snoise(st * 1.5 + u_time * 0.05);\n\
        float n2 = snoise(st * 3.0 - u_time * 0.03);\n\
        float noise = snoise(vec2(n1, n2) + st * 2.0 + u_time * 0.02);\n\
        float gradient = 1.0 - st.y - st.x * 0.5;\n\
        gradient = smoothstep(-0.5, 1.5, gradient);\n\
        vec3 lightGray = vec3(0.42, 0.43, 0.46);\n\
        vec3 deepGray  = vec3(0.13, 0.14, 0.16);\n\
        float mixFactor = smoothstep(-0.2, 0.8, noise + gradient * 0.5);\n\
        vec3 lightColor = mix(deepGray, lightGray, noise * 0.8 + 0.2);\n\
        float intensity = smoothstep(0.2, 1.0, mixFactor * gradient);\n\
        intensity *= 0.8 + 0.2 * sin(u_time * 0.5 + st.x * 5.0);\n\
        color += lightColor * intensity;\n\
        gl_FragColor = vec4(color, 1.0);\n\
      }\n\
    ';

    function createShader(type, source) {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('[yd-about-bg-canvas] shader compile:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    }

    const vs = createShader(gl.VERTEX_SHADER, vsSource);
    const fs = createShader(gl.FRAGMENT_SHADER, fsSource);
    if (!vs || !fs) return;

    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('[yd-about-bg-canvas] program link:', gl.getProgramInfoLog(program));
      return;
    }
    gl.useProgram(program);

    const positions = new Float32Array([
      -1, -1,  1, -1, -1,  1,
      -1,  1,  1, -1,  1,  1
    ]);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    const posLoc = gl.getAttribLocation(program, 'aVertexPosition');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const resLoc = gl.getUniformLocation(program, 'u_resolution');
    const timeLoc = gl.getUniformLocation(program, 'u_time');

    // 噪点渐变变化很慢，限 30fps：GPU 开销减半，肉眼无差别
    const FRAME_INTERVAL = 1000 / 30;
    const start = performance.now();
    let last = -Infinity;
    let rafId = 0;
    function render(now) {
      rafId = requestAnimationFrame(render);
      if (now - last < FRAME_INTERVAL) return;
      last = now;
      gl.uniform2f(resLoc, canvas.width, canvas.height);
      gl.uniform1f(timeLoc, (now - start) / 1000);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }
    rafId = requestAnimationFrame(render);

    // 主题编辑器卸载 section 时停止渲染并释放 WebGL 上下文
    canvas._ydAboutDestroy = function () {
      cancelAnimationFrame(rafId);
      ac.abort();
      const lose = gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
      delete canvas.dataset.ydAboutStarted;
    };
  }

  function init(scope) {
    scope = scope || document;
    const canvases = scope.querySelectorAll('canvas[data-yd-about-bg-canvas]');
    canvases.forEach(startBgCanvas);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('canvas[data-yd-about-bg-canvas]').forEach(function (c) {
      if (c._ydAboutDestroy) c._ydAboutDestroy();
    });
  });
})();
